import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, X } from "lucide-react";

/**
 * PriceDropTicker — narrow launch-price ticker at the very top of every
 * page announcing the ₹99 launch price. Single non-rotating message —
 * removed the "was ₹599 / Save ₹400" anchor per PK direction (no fake
 * anchors). Dismissible per-session (sessionStorage — not localStorage —
 * so the ticker returns each day).
 *
 * Positioned above the sticky app header via a plain relative wrapper so
 * the header still sticks correctly after the ticker scrolls out of view.
 *
 * NEET-route suppression (Feb 2026): the banner is MHT-CET-specific.
 * Suppress on NEET routes so a NEET user landing on /neet/instant isn't
 * confused about which product/price they're looking at.
 */
// Single launch-price message — no fake "was ₹599" anchor, no rotation.
// Keep in sync with services.pricing constants and the gateway env var.
const MESSAGE = "College prediction — ₹99, limited-time launch price";

const DISMISS_KEY = "dhruva_price_drop_ticker_dismissed_v3";

export default function PriceDropTicker() {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    // no-op — retained hook slot so future kill-switch logic can attach here.
  }, []);

  // Suppress on NEET routes, the redesigned neutral landing preview, and
  // exam-specific guides — banner copy is CET-only.
  const isNeetRoute = location.pathname.startsWith("/neet");
  const isLandingV2 = location.pathname.startsWith("/landing-v2");
  const isNeetGuide = location.pathname.startsWith("/guides/neet");
  if (isNeetRoute || isLandingV2 || isNeetGuide) return null;

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  return (
    <div
      className="relative w-full bg-gradient-to-r from-rose-600 via-amber-500 to-rose-600 text-white text-center text-[12.5px] sm:text-[13px] font-semibold py-2 px-8 overflow-hidden"
      data-testid="price-drop-ticker"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-center gap-2 max-w-4xl mx-auto">
        <Sparkles className="w-3.5 h-3.5 flex-shrink-0 hidden sm:inline" />
        <span
          className="tracking-tight"
          data-testid="price-drop-msg"
        >
          {MESSAGE}
        </span>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss price-drop announcement"
        className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-white/20 flex items-center justify-center text-white/90 hover:text-white transition-colors"
        data-testid="price-drop-ticker-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
